import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import ColumnSeparator from '../../../../core/layout/ColumnSeparator';
import Row from '../../../../core/layout/Row';
import Column from '../../../../core/layout/Column';
import TextInput from '../../../../core/form/TextInput';
import Button from '../../../../core/form/Button';
import Select from '../../../../core/form/Select';
import Table from '../../../../core/form/Table';
import AudioPreview from '../../../../core/layout/AudioPreview';
import ImagePreview from '../../../../core/layout/ImagePreview';
import ErrorText from '../../../../core/layout/ErrorText';
import SpellCheckInputContainer from './SpellCheckInputContainer';
import Separator from '../../../../core/layout/Separator';
import LocalFileInput from '../../../../core/form/LocalFileInput';
import FetchSelect from '../../../../core/form/FetchSelect';
import TextPreview from '../../../../core/layout/TextPreview';

const AnswersInput = (props) => (
  <div>
    {!props.disabled && (
      <Row>
        <Column size={props.answerType === 'BOTH' ? 7 : 6}>
          <TextInput
            label={props.label}
            value={get(props.values, 'text', '')}
            onChange={(value) => props.onChange('text', value)}
            errorText={get(props.errors, 'text', '')}
          />
        </Column>
        {props.answerType === 'BOTH' && (
          <Column size={2}>
            <Select
              label="Is this a Correct answer ?"
              value={get(props.values, 'correct', '')}
              onChange={(value) => props.onChange('correct', value)}
              errorText={get(props.errors, 'correct', '')}
              options={[
                { value: false, label: 'No' },
                { value: true, label: 'Yes' },
              ]}
            />
          </Column>
        )}
      </Row>
    )}
    {!props.disabled && props.allowSpellCheck && (
      <SpellCheckInputContainer onChange={props.onChange} values={props.values} disabled={props.disabled} />
    )}
    {!props.disabled && props.allowAudio && (
      <Row>
        <Column size={3}>
          <FetchSelect
            showSearch
            url="characters"
            apiVersion="v3"
            isPaginated
            disabled={props.disabled}
            label="Character"
            value={get(props.values, 'character', '')}
            onChange={(character) => {
              props.onChange('character', character || null);
            }}
            errorText={get(props.errors, 'character', '')}
            resultTransformer={{
              text: 'name',
              value: 'id',
            }}
          />
        </Column>
        <Column size={3}>
          <LocalFileInput
            accept="audio"
            label="Upload an audio to the answer"
            onChange={(key) => props.onChange('audio', key)}
            value={get(props.values, 'audio', '')}
            errorText={get(props.errors, 'audio', '')}
          />
        </Column>
        {props.hasImage && (
          <Column size={3}>
            <LocalFileInput
              accept="image"
              cropSize={{ width: 960, height: 640 }}
              label="Upload an image to the answer"
              onChange={(key) => props.onChange('image', key)}
              value={get(props.values, 'image', '')}
              errorText={get(props.errors, 'image', '')}
            />
          </Column>
        )}
      </Row>
    )}
    {props.hasImage && (
      <Row>
        <Column size={12}>
          <TextInput
            label="Image Prompt"
            fieldType="textarea"
            textAreaRows="4"
            disabled={props.disabled}
            value={get(props.values, 'imagePrompt', '')}
            onChange={(value) => props.onChange('imagePrompt', value)}
            errorText={get(props.errors, 'imagePrompt', '')}
          />
        </Column>
      </Row>
    )}
    {!props.disabled && (
      <Row>
        <Column size={12}>
          <Button
            icon="check"
            type="primary"
            label={props.values.id ? 'Update' : 'Add'}
            disabled={!props.isDirty()}
            onClick={() => props.onSubmit()}
          />
          <ColumnSeparator size="xs" />
          <Button icon="reload" label="Discard" onClick={() => props.onReset()} />
        </Column>
      </Row>
    )}
    <Separator size="sm" />
    <Row>
      <Column size={12}>
        <Table
          columns={[
            {
              label: 'Answer',
              path: 'text',
              width: '350px',
            },
            {
              label: 'Correct',
              path: 'correct',
              width: '105px',
              render: (cell) => (cell ? 'Yes' : 'No'),
            },
            ...(props.allowAudio
              ? [
                  {
                    label: 'Character',
                    render: (cell, row) => {
                      return row.character;
                    },
                  },
                ]
              : []),
            ...(props.allowAudio
              ? [
                  {
                    label: 'Audio',
                    render: (cell, row) => {
                      if (row.audio) {
                        return <AudioPreview src={row.audio} />;
                      }
                      return 'No Audio uploaded';
                    },
                  },
                ]
              : []),
            {
              label: 'Image',
              render: (cell, row) => {
                if (row.image) {
                  return <ImagePreview src={row.image} />;
                } else if (props.type === 'SINGLE_CHOICE_IMAGE') {
                  return 'No Image uploaded';
                }
                return '-';
              },
            },
            ...(props.hasImage
              ? [
                  {
                    label: 'Image Prompt',
                    path: 'imagePrompt',
                    render: (cell) => <TextPreview text={cell} maxCharacters={100} />,
                  },
                ]
              : []),
            {
              label: 'Actions',
              path: 'action',
              width: '105px',
              render: (cell, row) => {
                if (!props.disabled) {
                  return (
                    <div>
                      <Button icon="delete" onClick={() => props.onDelete(row.id)} />{' '}
                      <Button icon="edit" onClick={() => props.onEdit(row.id)} />
                    </div>
                  );
                }
                return null;
              },
            },
          ]}
          rows={props.answers}
        />
        {props.errorText && <ErrorText>{props.errorText}</ErrorText>}
      </Column>
    </Row>
  </div>
);

AnswersInput.propTypes = {
  answers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      correct: PropTypes.bool.isRequired,
    })
  ),
  answerType: PropTypes.oneOf(['CORRECT', 'WRONG', 'BOTH']).isRequired,
  disabled: PropTypes.bool,
  hasImage: PropTypes.bool,
  allowSpellCheck: PropTypes.bool,
  allowSpellAudio: PropTypes.bool,
  label: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['SINGLE_CHOICE_TEXT', 'SINGLE_CHOICE_AUDIO', 'SINGLE_CHOICE_IMAGE', 'SINGLE_CHOICE_GAME']),
  errorText: PropTypes.string,
  values: PropTypes.object,
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  isDirty: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

AnswersInput.defaultProps = {
  answers: [],
  values: {},
  errors: {},
  errorText: null,
  type: null,
  disabled: false,
  hasImage: false,
  allowSpellCheck: false,
  allowAudio: true,
};

export default AnswersInput;
